import { Outlet, useLoaderData, useRouteLoaderData } from "react-router-dom";
import PageTitle from "@/components/PageTitle";
import { GetFormsResponse } from "@/types/api/forms";
import Grid from "@mui/material/Grid";
import Form from "@/components/Form";
import { useFormsRefetch } from "@/routes/Forms/useFormsRefetch";
import { Alert, Box, Typography, useTheme } from "@mui/material";
import React from "react";
import RegenerateAll from "@/components/RegenerateAll";
import EnableFormsStep from "@/components/EnableFormsStep";
import { useAccountProvider } from "@/providers/hooks";
import type { RootLoader } from "@/types/RootLoader";

function Forms() {
  const { forms } = useLoaderData() as GetFormsResponse;

  useFormsRefetch(forms);

  const activeForms = forms.filter((form) => form.formStatus === "ACTIVE");
  const formName = activeForms.map((form) => form.name);
  const theme = useTheme();
  const { accountInfo } = useRouteLoaderData("root") as RootLoader;
  const isShopify = useAccountProvider() === "shopify";

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <PageTitle title="Forms" />
        <RegenerateAll
          method="post"
          action="/main/forms"
          confirmationMessage="Do you want to retrain tinyAlbert with updated Forms? Doing this may recreate some or all of your
        templates"
          successMessage='You have successfully "Albertized" your forms.'
          id="forms-overview-regenerate-all"
        />
      </Box>
      {isShopify && <EnableFormsStep shop={accountInfo?.providerIdentifier} />}
      <Alert
        variant="outlined"
        severity="info"
        sx={{
          mb: 5,
          borderColor: `${theme.palette.primary[60] as string} !important`,
          "& .MuiAlert-icon": {
            ml: 2,
          },
        }}
      >
        <Typography variant="p3">
          Adding a form to your website will help you collect new subscribers.
          As your subscriber count grows, your plan may need an upgrade to
          accommodate the increased audience.&nbsp;
          <Typography variant="p3" color="grey.90" component="span">
            <a
              href="https://tinyalbert.ai/pricing"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Learn More About Pricing →
            </a>
          </Typography>
        </Typography>
      </Alert>
      <Grid
        container
        spacing={{ xs: 3, md: 5, lg: 5 }}
        columns={{ xs: 12, md: 12, lg: 12 }}
        id="form-cards-section"
      >
        {forms.map(({ enabled, ...formProps }) => (
          <Grid item xs={12} md={6} lg={4} key={formProps.id}>
            <Form {...formProps} active={enabled} activeForms={formName} />
          </Grid>
        ))}
      </Grid>
      <Outlet />
    </>
  );
}

export default Forms;
export * from "./loader";
export * from "./action";
